import { useTranslation } from "react-i18next";
import { Link } from "~/components/ui/link";
import { Button } from "~/components/ui/button";
import { WISTIA_MEDIA_IDS, wistiaBackgroundSrc } from "~/lib/wistia";
import { cn } from "~/lib/utils";

// 16px bold CTAs, 52px tall: equal grid columns on mobile, 190px wide from lg.
const ctaClassName = "h-[52px] w-full px-4 py-4 text-base leading-5 tracking-[0.16px] lg:w-[190px] lg:px-6";

// Copy and CTAs beside a square autoplaying Wistia clip; the last panel of the desktop
// section, so its 72px top padding is the gap to the performance panel above.
// Must render inside the .fleet token scope.
export function FleetTrackingMatters() {
    const { t } = useTranslation("fleet");

    return (
        <section aria-labelledby="fleet-tracking-matters-title" className="py-8 lg:pt-[72px] lg:pb-[60px]">
            <div className="wrap flex flex-col items-center gap-6 lg:flex-row lg:justify-between lg:gap-12">
                <div className="flex w-full flex-col gap-6 lg:max-w-[580px] lg:flex-1 lg:gap-8">
                    <div className="flex flex-col gap-2 lg:gap-4">
                        <h2
                            id="fleet-tracking-matters-title"
                            className="font-display text-text-heading text-xl leading-[1.15] font-extrabold lg:max-w-[397px] lg:text-4xl lg:leading-[1.1]"
                        >
                            {t("tracking_matters.title")}
                        </h2>
                        <div className="text-text-body-secondary lg:text-md flex flex-col gap-5 text-sm leading-5 lg:leading-[1.1]">
                            <p>{t("tracking_matters.body_1")}</p>
                            <p>{t("tracking_matters.body_2")}</p>
                        </div>
                    </div>
                    <div className="grid w-full grid-cols-2 gap-3 lg:flex lg:w-auto">
                        <Button asChild variant="primary-alt" className={cn(ctaClassName, "max-lg:order-last")}>
                            <Link data-sid="fleet-tracking-matters-build-quote" to="/fleet/quote">{t("tracking_matters.build_quote")}</Link>
                        </Button>
                        <Button asChild variant="secondary-alt" className={ctaClassName}>
                            <Link data-sid="fleet-tracking-matters-contact-sales" to="/contact">{t("tracking_matters.contact_sales")}</Link>
                        </Button>
                    </div>
                </div>

                {/* The clip is square; the frame is a touch wider than tall on mobile, so the
                    full-width square overflows top and bottom and is clipped to cover. */}
                <div className="bg-bg-alt relative aspect-[328/286] w-full max-w-[328px] shrink-0 overflow-hidden rounded-[5px] md:max-w-[480px] lg:aspect-[534/535] lg:w-[45.1%] lg:max-w-[534px] lg:rounded-2xl">
                    {/* `allow` already grants fullscreen, so the legacy `allowFullScreen` is omitted. */}
                    <iframe
                        src={wistiaBackgroundSrc(WISTIA_MEDIA_IDS.transformingFleetVehicles)}
                        title={t("tracking_matters.video_title")}
                        allow="autoplay; fullscreen"
                        loading="lazy"
                        className="absolute top-1/2 left-0 aspect-square w-full -translate-y-1/2 border-0"
                    />
                </div>
            </div>
        </section>
    );
}

export default FleetTrackingMatters;
